import { Inject, Injectable } from '@angular/core';

/**
 * interfaces
 */
import { IMqttAuthProviderSnapshot, IMqttConnectionOptions } from './interfaces';

/**
 * services/tokens
 */
import { SessionOrchestratorService } from '../services';
import { MqttClientService } from './mqtt-client.service';
import { MQTT_OPTIONS, MQTT_OPTIONS_AUTH_PROVIDER } from './providers-tokens/providers';

/**
 * Internal Core Service for MQTT Orchestration.
 * @class MqttCoreService
 * @description Serves as the central registry for all active MqttClientService instances.
 * It manages the lifecycle of multiple MQTT clients if the application requires connecting to multiple brokers.
 * Typically used internally by the MQTT module, not directly by feature developers.
 */
@Injectable()
export class MqttCoreService {

  /**
   * Collection of active MqttClientService instances.
   * Uses a Set to ensure uniqueness.
   * @private
   */
  private _collectionMqttClients: Set<MqttClientService> = new Set();
  
  /**
   * Getter for the collection of active MqttClientService instances.
   * @public
   * @returns {Set<MqttClientService>} The set of active clients.
   */
  public get mqttClients(): Set<MqttClientService> {
    return this._collectionMqttClients;
  }

  /**
   * Getter for the number of active MqttClientService instances.
   * @public
   * @returns {number} The count of active clients.
   */
  public get mqttClientLength(): number {
    return this._collectionMqttClients.size;
  }

  /**
   * Creates an instance of MqttCoreService.
   * @constructor
   * @param {IMqttConnectionOptions} options - The default connection options injected via DI.
   * @param {IMqttAuthProviderSnapshot} [authProvider] - The optional authentication provider.
   */
  constructor(
    public readonly _sessionOrchestrator: SessionOrchestratorService,
    @Inject(MQTT_OPTIONS)
    public readonly options: IMqttConnectionOptions,
    @Inject(MQTT_OPTIONS_AUTH_PROVIDER)
    public readonly authProvider?: IMqttAuthProviderSnapshot,
  ) {

    // Subscribe to the session orchestrator global and destroy the mqtt clients when the global is aborted
    // This is to ensure that the mqtt clients are closed when the session is aborted
    this._sessionOrchestrator
        .global$.
        subscribe(() => this.destroy());

  }

  /**
   * Registers a new MqttClientService instance to the collection.
   * @public
   * @param {MqttClientService} mqttClient - The client instance to add.
   * @description Prevents duplicates and ensures the argument is a valid MqttClientService.
   */
  public addMqttClient(mqttClient: MqttClientService): void {
    
    // Check if the mqttClient is an instance of MqttClientService and if it is not already in the collection
    if (!(mqttClient instanceof MqttClientService) || this._collectionMqttClients.has(mqttClient) ) {
      return;
    }

    // Add the mqttClient to the collection
    this._collectionMqttClients.add(mqttClient);

  }

  /**
   * Unregisters an MqttClientService instance from the collection.
   * @public
   * @param {MqttClientService} mqttClient - The client instance to remove.
   */
  public removeMqttClient(mqttClient: MqttClientService): void {

    // Check if the mqttClient is an instance of MqttClientService and if it is in the collection
    if (!(mqttClient instanceof MqttClientService) || !this._collectionMqttClients.has(mqttClient) ) {
      return;
    }

    // Remove the mqttClient from the collection
    this._collectionMqttClients.delete(mqttClient);

  }

  /**
   * Destroys all registered MQTT clients and clears the collection.
   * Should be called when the module or application is being destroyed to ensure all sockets are closed.
   * @public
   */
  public destroy(): void {

    // Destroy all the mqtt clients
    this._collectionMqttClients
          .forEach(mqttClient => mqttClient.destroy());

    // Clear the collection of mqtt clients
    this._collectionMqttClients.clear();

  }
}
